// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// DRAFT AI-assistance notice (IP, License & Attribution Policy v1.3 §12d-1):
// This content was prepared with the assistance of AI tools and is, on this
// branch, a DRAFT pending human review. On the human-approved merge of its
// Merge Request this notice flips to the published variant (§12d-2).
// Bu icerik yapay zeka destekli araclarla hazirlanmis bir TASLAKTIR (§12d-1);
// insan onayiyla birlestirilmesinde yayimlanmis surumune (§12d-2) donusur.

/**
 * Bridge message envelope (adapted from AU0008 ADR-005 D1/D2).
 *
 * Two envelope kinds travel over postMessage, both JSON-serializable plain
 * objects. Discriminated on a first-class `ok` so callers branch on an explicit
 * terminal state and never infer success from the shape of the payload
 * (ADR-002 P5). Additive fields (protocolVersion) interoperate with the current
 * Gateway reply shape, which the client normalizes on its side.
 */

import type { BridgeError } from "./errors";

/**
 * The bridge protocol version: a single integer, bumped only on an INCOMPATIBLE
 * envelope/semantic change; additive fields do not bump it. M1 shipping value.
 */
export const PROTOCOL_VERSION = 1;

/** The call descriptor inside a request. */
export interface CallDescriptor {
  /** The ZOHO.CREATOR module name. */
  readonly module: string;
  /** The function name within that module. */
  readonly function: string;
  /** Optional argument object passed to the invoked function. */
  readonly config?: Readonly<Record<string, unknown>>;
}

/** Request envelope: widget -> Gateway. */
export interface RequestEnvelope {
  readonly protocolVersion: number;
  /** Per-request correlation id, crypto.randomUUID(). */
  readonly messageUUID: string;
  readonly message: CallDescriptor;
}

/**
 * Response envelope: Gateway -> widget. `protocolVersion` and the first-class
 * `ok`/`error` are the target fields; the current Gateway may not emit them, so
 * on input they are treated as optional and the client normalizes the legacy
 * reply on its side. Discriminated on `ok` (ADR-002 P5).
 */
export type ResponseEnvelope =
  | {
      readonly protocolVersion?: number;
      readonly messageUUID: string;
      readonly ok: true;
      readonly result: unknown;
    }
  | {
      readonly protocolVersion?: number;
      readonly messageUUID: string;
      readonly ok: false;
      readonly error: BridgeError;
    };

/** Build a well-formed request envelope for a call. */
export function makeRequestEnvelope(
  messageUUID: string,
  call: CallDescriptor,
): RequestEnvelope {
  return { protocolVersion: PROTOCOL_VERSION, messageUUID, message: call };
}

/**
 * Extract a valid messageUUID from an unknown inbound message, or null. A reply
 * is only actioned once BOTH the origin check (transport) and this correlation
 * check pass. Full outcome interpretation is done by interpretResponse once the
 * reply is matched to an outstanding request.
 */
export function readMessageUUID(value: unknown): string | null {
  if (typeof value !== "object" || value === null) return null;
  const uuid = (value as Record<string, unknown>)["messageUUID"];
  return typeof uuid === "string" && uuid.length > 0 ? uuid : null;
}

/**
 * The outcome of interpreting a matched inbound reply, normalizing the current
 * Gateway's legacy shape where needed. Discriminated on `ok`; a malformed reply
 * is itself an explicit failure.
 */
export type InterpretedResponse =
  | { readonly ok: true; readonly result: unknown }
  | { readonly ok: false; readonly error: BridgeError };
